'use client';

import type { Cart, CartLine, Product, ProductVariant } from '@/lib/shopify/types';
import { statsAddToCart, statsBeginCheckout, statsConsent, statsRemoveFromCart } from '@/lib/stats';
import {
  initializeMetaPixel,
  metaAddToCart,
  metaInitiateCheckout,
  metaPixelEnabled,
  readMarketingConsent,
  readMetaBrowserIds,
} from '@/lib/meta-pixel';

export type AnalyticsConsent = 'granted' | 'denied' | null;

export const ANALYTICS_CONSENT_KEY = 'nomafe_analytics_consent_v1';
export const ANALYTICS_CONSENT_EVENT = 'nomafe:analytics-consent';

const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();
let initialized = false;

type GtagCommand = [command: string, ...args: unknown[]];

declare global {
  interface Window {
    dataLayer?: GtagCommand[];
    gtag?: (...args: GtagCommand) => void;
  }
}

const consentSettings = (analyticsStorage: 'granted' | 'denied') => ({
  analytics_storage: analyticsStorage,
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
});

function ensureDataLayer() {
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js only processes `arguments` objects pushed to the dataLayer, not plain arrays
  window.gtag =
    window.gtag ??
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments as unknown as GtagCommand);
    };
}

export function readAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === 'undefined') return null;
  const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
  return value === 'granted' || value === 'denied' ? value : null;
}

export function initializeAnalytics() {
  if (typeof window === 'undefined' || initialized || !measurementId) return;
  if (readAnalyticsConsent() !== 'granted') return;

  initialized = true;
  captureUtm(); // landing UTMs, kept for the cart attributes (only with consent)
  ensureDataLayer();
  window.gtag?.('consent', 'default', consentSettings('granted'));
  window.gtag?.('js', new Date());
  window.gtag?.('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  if (!document.querySelector(`script[data-nomafe-ga4="${measurementId}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.dataset.nomafeGa4 = measurementId;
    document.head.appendChild(script);
  }
}

function expireGoogleAnalyticsCookies() {
  const names = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter((name) => name === '_ga' || name?.startsWith('_ga_'));

  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.${window.location.hostname}; SameSite=Lax`;
  }
}

export function updateAnalyticsConsent(consent: Exclude<AnalyticsConsent, null>) {
  window.localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
  statsConsent(consent, 'a');

  if (consent === 'granted') {
    initializeAnalytics();
    window.gtag?.('consent', 'update', consentSettings('granted'));
  } else {
    if (window.gtag) window.gtag('consent', 'update', consentSettings('denied'));
    expireGoogleAnalyticsCookies();
  }

  window.dispatchEvent(new CustomEvent(ANALYTICS_CONSENT_EVENT, { detail: consent }));
}

function event(name: string, parameters: Record<string, unknown>) {
  if (readAnalyticsConsent() !== 'granted' || !measurementId) return;
  initializeAnalytics();
  window.gtag?.('event', name, { ...parameters, transport_type: 'beacon' });
}

const amount = (value: string) => Number(value);

const productItem = (product: Product, variant: ProductVariant, quantity: number) => ({
  item_id: variant.id,
  item_name: product.title,
  item_brand: 'Nomafé',
  item_variant: variant.title,
  price: amount(variant.price.amount),
  quantity,
});

const cartItems = (cart: Cart) =>
  cart.lines.map((line) => ({
    item_id: line.merchandise.id,
    item_name: line.merchandise.product.title,
    item_brand: 'Nomafé',
    item_variant: line.merchandise.title,
    price: amount(line.merchandise.price.amount),
    quantity: line.quantity,
  }));

export function trackPageView() {
  event('page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
  });
}

export function trackViewItem(product: Product, variant: ProductVariant) {
  event('view_item', {
    currency: variant.price.currencyCode,
    value: amount(variant.price.amount),
    items: [productItem(product, variant, 1)],
  });
}

/** `cart` = the cart Shopify returned after the add (Meta takes the real, discounted value from it). */
export function trackAddToCart(product: Product, variant: ProductVariant, quantity: number, cart?: Cart | null) {
  statsAddToCart(product.id, quantity, variant.id); // own aggregated stats: independent of GA4 consent
  metaAddToCart(product, variant, quantity, cart); // Meta Pixel: its own (marketing) consent
  event('add_to_cart', {
    currency: variant.price.currencyCode,
    value: amount(variant.price.amount) * quantity,
    items: [productItem(product, variant, quantity)],
  });
}

export function trackBeginCheckout(cart: Cart) {
  statsBeginCheckout();
  metaInitiateCheckout(cart); // Meta Pixel: its own (marketing) consent
  event('begin_checkout', {
    currency: cart.cost.subtotalAmount.currencyCode,
    value: amount(cart.cost.subtotalAmount.amount),
    items: cartItems(cart),
  });
}

const lineItem = (line: CartLine, quantity: number) => ({
  item_id: line.merchandise.id,
  item_name: line.merchandise.product.title,
  item_brand: 'Nomafé',
  item_variant: line.merchandise.title,
  price: amount(line.merchandise.price.amount),
  quantity,
});

/** `quantity` is the number of units actually removed (the real difference confirmed by Shopify). */
export function trackRemoveFromCart(line: CartLine, quantity: number, productId?: string) {
  if (quantity < 1) return;
  statsRemoveFromCart(productId, quantity);
  event('remove_from_cart', {
    currency: line.merchandise.price.currencyCode,
    value: amount(line.merchandise.price.amount) * quantity,
    items: [lineItem(line, quantity)],
  });
}

// ---------- Cart attributes for server-side purchase attribution ----------
// Only with analytics consent: Shopify's checkout runs on another domain, so the GA4 client/session
// ids and the UTMs travel in the cart and come back in the paid order. No personal data.

export type CartAttribute = { key: string; value: string };

const ANALYTICS_ATTRIBUTE_KEYS = new Set([
  'analytics_consent',
  'marketing_consent',
  'meta_fbp',
  'meta_fbc',
  'site',
  'ga_client_id',
  'ga_session_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]);

export const isAnalyticsAttribute = (attribute: CartAttribute) => ANALYTICS_ATTRIBUTE_KEYS.has(attribute.key);

/** Same keys and values, in any order. */
export function sameAttributes(a: CartAttribute[], b: CartAttribute[]) {
  if (a.length !== b.length) return false;
  const wanted = new Map(b.map((x) => [x.key, x.value]));
  return a.every((x) => wanted.get(x.key) === x.value);
}

const UTM_KEY = 'nomafe_utm_v1';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

/** Remembers the UTMs of the landing (first touch of the session); only called with consent. */
export function captureUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found: Record<string, string> = {};
    for (const key of UTM_PARAMS) {
      const value = params.get(key)?.trim().slice(0, 100);
      if (value) found[key] = value;
    }
    if (Object.keys(found).length && !window.sessionStorage.getItem(UTM_KEY)) {
      window.sessionStorage.setItem(UTM_KEY, JSON.stringify(found));
    }
  } catch {
    /* storage unavailable: no UTMs, nothing else changes */
  }
}

function gtagGet(field: 'client_id' | 'session_id'): Promise<string | null> {
  return new Promise((resolve) => {
    if (!window.gtag || !measurementId) return resolve(null);
    const timer = setTimeout(() => resolve(null), 1200);
    window.gtag('get', measurementId, field, (value: unknown) => {
      clearTimeout(timer);
      resolve(value === undefined || value === null || value === '' ? null : String(value));
    });
  });
}

/**
 * `null` when there is no consent at all: nothing analytical or advertising must be stored in the cart.
 * Each block depends on its own consent: analytics (GA4 ids, UTMs) and marketing (Meta browser ids).
 */
export async function collectCartAttributes(): Promise<CartAttribute[] | null> {
  if (typeof window === 'undefined') return null;
  const analytics = Boolean(measurementId) && readAnalyticsConsent() === 'granted';
  const marketing = metaPixelEnabled && readMarketingConsent() === 'granted';
  if (!analytics && !marketing) return null;

  const attributes: CartAttribute[] = [{ key: 'site', value: 'nomafe.com' }];

  if (analytics) {
    initializeAnalytics();
    captureUtm();
    attributes.push({ key: 'analytics_consent', value: 'granted' });
    const [clientId, sessionId] = await Promise.all([gtagGet('client_id'), gtagGet('session_id')]);
    if (clientId) attributes.push({ key: 'ga_client_id', value: clientId });
    if (sessionId) attributes.push({ key: 'ga_session_id', value: sessionId });
    try {
      const utm = JSON.parse(window.sessionStorage.getItem(UTM_KEY) ?? '{}') as Record<string, string>;
      for (const key of UTM_PARAMS) if (utm[key]) attributes.push({ key, value: utm[key] });
    } catch {
      /* ignore */
    }
  }

  if (marketing) {
    initializeMetaPixel();
    attributes.push({ key: 'marketing_consent', value: 'granted' });
    const { fbp, fbc } = readMetaBrowserIds();
    if (fbp) attributes.push({ key: 'meta_fbp', value: fbp });
    if (fbc) attributes.push({ key: 'meta_fbc', value: fbc });
  }
  return attributes;
}
